import classNames from 'classnames';
import Image from 'next/image';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import WebHeaderDropDownLink from '@alltrails/amplitude/enums/WebHeaderDropDownLink';
import WebHeaderSection from '@alltrails/amplitude/enums/WebHeaderSection';
import logTripPlannerCTAClicked from '@alltrails/amplitude/events/logTripPlannerCTAClicked';
import logTripPlannerCTAViewed from '@alltrails/amplitude/events/logTripPlannerCTAViewed';
import logWebHeaderDropDownTapped from '@alltrails/amplitude/events/logWebHeaderDropDownTapped';
import { userHasPermission } from '@alltrails/context/hooks/useAuthorization';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import getFullName from '@alltrails/core/utils/getFullName';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Avatar from '@alltrails/denali/components/Avatar';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import Activity from '@alltrails/denali/icons/Activity';
import ArrowExternal from '@alltrails/denali/icons/ArrowExternal';
import Calendar from '@alltrails/denali/icons/Calendar';
import Chevron from '@alltrails/denali/icons/Chevron';
import Community from '@alltrails/denali/icons/Community';
import DrawPath from '@alltrails/denali/icons/DrawPath';
import Gift from '@alltrails/denali/icons/Gift';
import Heart from '@alltrails/denali/icons/Heart';
import Help from '@alltrails/denali/icons/Help';
import Logout from '@alltrails/denali/icons/Logout';
import NationalPark from '@alltrails/denali/icons/NationalPark';
import PlaceTrail from '@alltrails/denali/icons/PlaceTrail';
import QrCode from '@alltrails/denali/icons/QrCode';
import Settings from '@alltrails/denali/icons/Settings';
import Shop from '@alltrails/denali/icons/Shop';
import Sparkle from '@alltrails/denali/icons/Sparkle';
import Tag from '@alltrails/denali/icons/Tag';
import Time from '@alltrails/denali/icons/Time';
import { DropdownMenuOption, DropdownMenuTextOption, LinkInfo } from '@alltrails/denali/types';
import type Experiments from '@alltrails/types/Experiments';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { User } from '@alltrails/types/User';
import { isTripPlannerEntryPointVisible } from '../../utils/tripPlannerAvailability';
import { getTripPlannerEntryParams, type TripPlannerEntryPoint } from '../../utils/tripPlannerEntryParams';
import peakMenuBadgeSrc from './menuBadges/peak-menu-badge.svg';
import plansMenuBadgeSrc from './menuBadges/plans-menu-badge.svg';
import styles from './styles/styles.module.scss';

export const headerLinkTrigger = 'nav';

const tripPlannerNavEntryPoint: TripPlannerEntryPoint = 'web explore nav menu';

type HeaderLinkBase = Pick<DropdownMenuTextOption, 'className' | 'leadingIcon' | 'onClick' | 'subtext' | 'title' | 'trailingIcon'> & {
  webHeaderDropDownLink: WebHeaderDropDownLink;
  // Called when the menu containing this link is opened, for links that log their own impression event.
  onView?: () => void;
};

export type AuthRequiredHeaderLink = HeaderLinkBase & {
  requiresAuth: true;
  getLinkInfo: (user: User) => LinkInfo;
  trigger: CarouselDisplayTrigger;
  upsellTrigger: UpsellTrigger;
};

export type BasicHeaderLink = HeaderLinkBase & { requiresAuth?: never; linkInfo?: LinkInfo };

export type HeaderLink = AuthRequiredHeaderLink | BasicHeaderLink;

export type HeaderSection = {
  id: string;
  label: ReactNode;
  links: HeaderLink[];
  webHeaderSection: WebHeaderSection;
};

type GetHeaderLinkArgs = {
  inUS: boolean;
  irclickid?: string;
  isMobile?: boolean;
  pathname?: string;
  languageRegionCode: LanguageRegionCode;
  user: User | null;
  experiments: Experiments;
};

export const getHeaderLinks = ({
  inUS,
  irclickid,
  isMobile,
  languageRegionCode,
  pathname,
  user,
  experiments
}: GetHeaderLinkArgs): HeaderSection[] => {
  const includeTripPlanner = isTripPlannerEntryPointVisible({ experiments, languageRegionCode, user });

  const createXpValue = experiments['web-disco-rm-nav-create']?.value;
  // On mobile, the nav create xp should not have any impact
  const showCreateSection = !isMobile && createXpValue === 'dropdown';
  const hasStandaloneCreateRouteButton =
    !isMobile && (createXpValue === 'button-secondary' || createXpValue === 'button-tertiary' || createXpValue === 'button-text');

  const createCustomRouteLink: HeaderLink = {
    leadingIcon: { icon: { Component: DrawPath }, tile: true },
    // 'trigger=nav' is used to a) pass the correct trigger and upsell trigger to the SignUpModal, and b) reset the state for new custom routes
    // 'returnTo' is used to pass the current pathname to the SignUpModal so that the user can
    // be redirected back to the current page if they don't authenticate
    linkInfo: {
      href: wrapUrlSafe(`/explore/custom-routes/new?trigger=${headerLinkTrigger}${pathname ? `&returnTo=${pathname}` : ''}`, languageRegionCode)
    },
    title: showCreateSection ? <FormattedMessage defaultMessage="Custom route" /> : <FormattedMessage defaultMessage="Build custom route" />,
    webHeaderDropDownLink: 'custom route'
  };

  const createTripLink: HeaderLink = {
    leadingIcon: { icon: { Component: Sparkle }, tile: true },
    linkInfo: {
      href: wrapUrlSafe(
        `/trip-assistant?${new URLSearchParams(getTripPlannerEntryParams({ entryPoint: tripPlannerNavEntryPoint }))}`,
        languageRegionCode
      )
    },
    onClick: () => logTripPlannerCTAClicked({ entry_point: tripPlannerNavEntryPoint }),
    onView: () => logTripPlannerCTAViewed({ entry_point: tripPlannerNavEntryPoint }),
    // TODO: translate once out of POC https://alltrails.atlassian.net/browse/DISCO-26230
    title: showCreateSection ? 'Trip' : 'Create a trip',
    // TODO: add a dedicated 'create a trip' value to WebHeaderDropDownLink once out of POC
    webHeaderDropDownLink: 'custom route' as const
  };

  const exploreSection: HeaderSection = {
    id: 'explore',
    label: <FormattedMessage defaultMessage="Explore" />,
    links: [
      {
        leadingIcon: { icon: { Component: PlaceTrail }, tile: true },
        linkInfo: { href: wrapUrlSafe('/explore', languageRegionCode) },
        title: <FormattedMessage defaultMessage="Nearby trails" />,
        webHeaderDropDownLink: 'nearby trails'
      },
      {
        leadingIcon: { icon: { Component: Time }, tile: true },
        getLinkInfo: () => ({ href: wrapUrlSafe('/explore/recently-viewed', languageRegionCode) }),
        requiresAuth: true,
        title: <FormattedMessage defaultMessage="Recently viewed" />,
        trigger: 'recently viewed',
        upsellTrigger: 'recently viewed',
        webHeaderDropDownLink: 'recently viewed'
      },
      ...(showCreateSection || hasStandaloneCreateRouteButton ? [] : [createCustomRouteLink]),
      ...(includeTripPlanner && !showCreateSection ? [createTripLink] : []),
      {
        leadingIcon: { icon: { Component: Community }, tile: true },
        linkInfo: { href: wrapUrlSafe('/community', languageRegionCode) },
        title: <FormattedMessage defaultMessage="Community" />,
        webHeaderDropDownLink: 'community'
      },
      {
        leadingIcon: { icon: { Component: NationalPark }, tile: true },
        linkInfo: { href: wrapUrlSafe('/guides', languageRegionCode) },
        title: <FormattedMessage defaultMessage="National park guides" />,
        webHeaderDropDownLink: 'national park guides'
      }
    ],
    webHeaderSection: 'explore'
  };

  const createListLink: HeaderLink = {
    leadingIcon: { icon: { Component: Heart }, tile: true },
    title: showCreateSection ? <FormattedMessage defaultMessage="List" /> : <FormattedMessage defaultMessage="Create a list" />,
    requiresAuth: true,
    getLinkInfo: ({ slug }) => ({
      href: wrapUrlSafe(`/members/${slug}/lists`, languageRegionCode)
    }),
    trigger: 'Create List',
    upsellTrigger: 'Create List',
    webHeaderDropDownLink: 'create a list'
  };

  const createSection: HeaderSection | undefined = showCreateSection
    ? {
        id: 'create',
        label: <FormattedMessage defaultMessage="Create" />,
        links: [createListLink, createCustomRouteLink, ...(includeTripPlanner ? [createTripLink] : [])],
        webHeaderSection: 'create'
      }
    : undefined;

  const customRoutesLink: AuthRequiredHeaderLink = {
    leadingIcon: { icon: { Component: DrawPath }, tile: true },
    requiresAuth: true,
    getLinkInfo: ({ slug }) => ({
      href: wrapUrlSafe(`/members/${slug}/custom-routes`, languageRegionCode),
      isNativeLink: true // Next.js has a bug that throws a Cannot read properties of undefined (reading '0') trying to access next-router-state-tree on cross app navigation
    }),
    title: <FormattedMessage defaultMessage="Custom routes & maps" />,
    webHeaderDropDownLink: 'custom route',
    trigger: 'custom routes global nav saved tapped',
    upsellTrigger: 'custom routes global nav saved tapped'
  };

  const savedLinks: HeaderLink[] =
    user || showCreateSection
      ? [
          {
            leadingIcon: { icon: { Component: Heart }, tile: true },
            requiresAuth: true,
            getLinkInfo: ({ slug }) => ({ href: wrapUrlSafe(`/members/${slug}/lists`, languageRegionCode) }),
            title: <FormattedMessage defaultMessage="Lists" />,
            webHeaderDropDownLink: 'view all lists',
            trigger: 'global nav lists tapped',
            upsellTrigger: 'global nav lists tapped'
          },
          customRoutesLink,
          {
            leadingIcon: { icon: { Component: Calendar }, tile: true },
            requiresAuth: true,
            getLinkInfo: ({ slug }) => ({ href: wrapUrlSafe(`/members/${slug}/invites`, languageRegionCode) }),
            title: <FormattedMessage defaultMessage="Invites" />,
            webHeaderDropDownLink: 'invites',
            trigger: 'global nav invites tapped',
            upsellTrigger: 'global nav invites tapped'
          }
        ]
      : [customRoutesLink, createListLink];
  const savedSection: HeaderSection = {
    id: 'saved',
    label: <FormattedMessage defaultMessage="Saved" />,
    links: savedLinks,
    webHeaderSection: 'save'
  };

  // Now that the main store has multipass, we would rather alpha users link to the sandbox
  // to avoid multiple unused alpha accounts signing up on the main store.
  // The sandbox is password protected and it can be found in 1Password.

  const gearShopLink = process.env.NEXT_PUBLIC_GEAR_SHOP_LINK;
  const memberDealsLink = process.env.NEXT_PUBLIC_MEMBER_DEALS_LINK;

  const shopLinks: HeaderLink[] = [];
  const buildShopUrl = (url: string) => `${url}${irclickid ? `?${new URLSearchParams({ irclickid })}` : ''}`;

  if (!isSubscriber(user)) {
    shopLinks.push({
      leadingIcon: { customElement: <Image src={plansMenuBadgeSrc} alt="" /> },
      linkInfo: { href: wrapUrlSafe('/plans', languageRegionCode) },
      title:
        !user || user.isPromoEligible ? (
          <FormattedMessage defaultMessage="Try Plus for free" />
        ) : (
          <FormattedMessage defaultMessage="Upgrade to Plus" />
        ),
      webHeaderDropDownLink: 'at plus'
    });
  }

  if (isSubscriber(user) && user?.subscriptionTier === 'plus') {
    shopLinks.push({
      leadingIcon: { customElement: <Image src={peakMenuBadgeSrc} alt="" /> },
      linkInfo: { href: wrapUrlSafe('/plans', languageRegionCode) },
      title: <FormattedMessage defaultMessage="Try Peak" />,
      webHeaderDropDownLink: 'at plus'
    });
  }

  shopLinks.push({
    leadingIcon: { icon: { Component: Gift }, tile: true },
    linkInfo: { href: wrapUrlSafe('/gift', languageRegionCode) },
    title: <FormattedMessage defaultMessage="Gift membership" />,
    webHeaderDropDownLink: 'give at plus'
  });
  if (inUS) {
    shopLinks.push({
      leadingIcon: { icon: { Component: Tag }, tile: true },
      linkInfo: { href: buildShopUrl(memberDealsLink) },
      title: 'Member Deals', // This is intentionally untranslated
      trailingIcon: { icon: { Component: ArrowExternal, color: '--color-text-secondary', size: 'xs' } },
      webHeaderDropDownLink: 'member deals'
    });
    shopLinks.push({
      leadingIcon: { icon: { Component: Shop }, tile: true },
      linkInfo: { href: buildShopUrl(gearShopLink) },
      title: 'Gear Shop', // This is intentionally untranslated (DISCO-10275)
      trailingIcon: { icon: { Component: ArrowExternal, color: '--color-text-secondary', size: 'xs' } },
      webHeaderDropDownLink: 'gear shop'
    });
  }
  const shopSection: HeaderSection = {
    id: 'shop',
    label: <FormattedMessage defaultMessage="Shop" />,
    links: shopLinks,
    webHeaderSection: 'shop'
  };

  return [exploreSection, ...(createSection ? [createSection] : []), savedSection, shopSection];
};

export const getUserLinks = ({
  user,
  languageRegionCode,
  includeLeadingIcons,
  isAdminImpersonating
}: {
  user: User;
  languageRegionCode: LanguageRegionCode;
  includeLeadingIcons?: boolean;
  isAdminImpersonating?: boolean;
}): HeaderLink[] => {
  const links: HeaderLink[] = [];
  const fullUserName = getFullName(user);
  links.push({
    leadingIcon: {
      customElement: <Avatar size="sm" userId={user.id} userName={fullUserName} />
    },
    linkInfo: { href: wrapUrlSafe(`/members/${user.slug}`, languageRegionCode) },
    subtext: <FormattedMessage defaultMessage="Show profile" />,
    title: fullUserName,
    trailingIcon: { icon: { Component: Chevron, orientation: 'right', size: 'sm' } },
    webHeaderDropDownLink: 'show profile'
  });

  links.push({
    leadingIcon: includeLeadingIcons ? { icon: { Component: Activity }, tile: true } : undefined,
    linkInfo: {
      href: wrapUrlSafe(`/explore/members/${user.slug}/recordings`, languageRegionCode),
      isNativeLink: true // Next.js has a bug that throws a Cannot read properties of undefined (reading '0') trying to access next-router-state-tree on cross app navigation
    },
    title: <FormattedMessage defaultMessage="Activities" />,
    webHeaderDropDownLink: 'activities'
  });

  if (user.subscriptionTier === 'plus') {
    links.push({
      className: classNames(styles.plusUserUpsellBackground, styles.upsellItem),
      linkInfo: { href: wrapUrlSafe('/plans', languageRegionCode) },
      title: <FormattedMessage defaultMessage="Unlock more with Peak" />,
      trailingIcon: { customElement: <PeakLogoBadge size="sm" variant="icon" /> },
      webHeaderDropDownLink: 'at plus'
    });
  } else if (!isSubscriber(user) && user.isPromoEligible) {
    links.push({
      className: classNames(styles.freeUserUpsellBackground, styles.upsellItem),
      linkInfo: { href: wrapUrlSafe('/plans', languageRegionCode) },
      title: <FormattedMessage defaultMessage="Try Plus for free" />,
      trailingIcon: { customElement: <PlusLogoBadge size="sm" variant="icon" /> },
      webHeaderDropDownLink: 'at plus'
    });
  } else if (!isSubscriber(user)) {
    links.push({
      className: classNames(styles.freeUserUpsellBackground, styles.upsellItem),
      linkInfo: { href: wrapUrlSafe('/plans', languageRegionCode) },
      title: <FormattedMessage defaultMessage="Upgrade to Plus" />,
      trailingIcon: { customElement: <PlusLogoBadge size="sm" variant="icon" /> },
      webHeaderDropDownLink: 'at plus'
    });
  }

  links.push(
    {
      leadingIcon: includeLeadingIcons ? { icon: { Component: Settings }, tile: true } : undefined,
      linkInfo: {
        href: wrapUrlSafe('/my/settings', languageRegionCode)
      },
      title: <FormattedMessage defaultMessage="Settings" />,
      webHeaderDropDownLink: 'settings'
    },
    {
      linkInfo: {
        href: wrapUrlSafe('/whats-new', languageRegionCode)
      },
      title: <FormattedMessage defaultMessage="What’s new" />,
      webHeaderDropDownLink: 'whats new'
    },
    {
      leadingIcon: includeLeadingIcons ? { icon: { Component: Help }, tile: true } : undefined,
      onClick: () => {
        if (window.adaEmbed) {
          window.adaEmbed.toggle();
        }
      },
      title: <FormattedMessage defaultMessage="Chat with support" />,
      webHeaderDropDownLink: 'chat with support'
    },
    {
      leadingIcon: includeLeadingIcons ? { icon: { Component: Logout }, tile: true } : undefined,
      linkInfo: { href: wrapUrlSafe('/sign_out', languageRegionCode), isNativeLink: true },
      onClick: () => {
        // Clear only the Amplitude user id (keep the device id) so experiment identity doesn't persist across logout.
        window.amplitude?.setUserId(undefined);
        window.Sprig?.('logoutUser');
      },
      title: isAdminImpersonating ? 'Stop impersonating' : <FormattedMessage defaultMessage="Log out" />,
      webHeaderDropDownLink: 'create a map'
    }
  );

  return links;
};

export const getUserPermissionLinks = (user: User, languageRegionCode: LanguageRegionCode): DropdownMenuOption[] => {
  const adminHeaderSection: DropdownMenuOption = {
    sectionHeader: <FormattedMessage defaultMessage="Admin" />
  };

  const partnersHeaderSection: DropdownMenuOption = {
    sectionHeader: <FormattedMessage defaultMessage="Partners" />
  };

  const internalToolsLink: DropdownMenuOption = {
    linkInfo: { href: wrapUrlSafe('/manage/search', languageRegionCode) },
    title: <FormattedMessage defaultMessage="Internal tools" />,
    trailingIcon: { icon: { Component: ArrowExternal, size: 'sm' } }
  };

  const publicLandsPortalLink: DropdownMenuOption = {
    linkInfo: { href: wrapUrlSafe('/portal', languageRegionCode) },
    title: <FormattedMessage defaultMessage="Public Lands portal" />,
    trailingIcon: { icon: { Component: ArrowExternal, size: 'sm' } }
  };

  if (
    userHasPermission(user, 'areas:manage') ||
    userHasPermission(user, 'locations:manage') ||
    userHasPermission(user, 'public_lands_onboarding:manage')
  ) {
    return [adminHeaderSection, internalToolsLink, publicLandsPortalLink];
  } else if (userHasPermission(user, 'guides:manage') || userHasPermission(user, 'press:manage') || userHasPermission(user, 'seo_tests:manage')) {
    return [adminHeaderSection, internalToolsLink];
  } else if (userHasPermission(user, 'public_lands:manage')) {
    return [partnersHeaderSection, publicLandsPortalLink];
  }

  return [];
};

export const getHeaderLinkOnClickWithAnalytics = (
  index: number,
  webHeaderDropDownLink: WebHeaderDropDownLink,
  webHeaderSection: WebHeaderSection,
  onClick?: () => void
) => {
  return () => {
    logWebHeaderDropDownTapped({ item_name: webHeaderDropDownLink, item_position: index, web_header_section: webHeaderSection });
    onClick?.();
  };
};

export const getUserDropdownOptions = ({
  user,
  languageRegionCode,
  isAdminImpersonating,
  onContinueInApp
}: {
  user: User;
  languageRegionCode: LanguageRegionCode;
  isAdminImpersonating?: boolean;
  onContinueInApp: () => void;
}): DropdownMenuOption[] => {
  const options: DropdownMenuOption[] = getUserLinks({ user, languageRegionCode, isAdminImpersonating });

  if (isSubscriber(user)) {
    options.splice(
      2,
      0,
      { sectionHeader: null },
      {
        onClick: onContinueInApp,
        title: <FormattedMessage defaultMessage="Continue in app" />,
        trailingIcon: { icon: { Component: QrCode, size: 'sm' } }
      },
      { sectionHeader: null }
    );
  }

  options.push(...getUserPermissionLinks(user, languageRegionCode));

  return options.map((option: HeaderLink | DropdownMenuOption, index) => {
    if ('webHeaderDropDownLink' in option) {
      const onClick = getHeaderLinkOnClickWithAnalytics(index, option.webHeaderDropDownLink, 'my account', option.onClick);
      return { ...option, onClick };
    }
    return option;
  });
};
